// Sugalarm — web command-station Dashboard (cycle 170).
//
// WEB FRAME ONLY (a guard sends every other variant to /now). The web
// landing surface, composed ENTIRELY from data the app already exposes —
// no new endpoints, no new WS frames. It shows the viewed (active) member
// header, the existing interactive <HistoryChart> as the live trace, the
// existing analytics summary (TIR + GMI/Avg/CV) over a selectable window,
// and client-side lows/highs EPISODE counts derived from the existing
// edge-triggered /api/alerts rows (urgent sub-counts surfaced).
//
// Cycle 177 adds two web-frame-only pieces on top of the cycle-170 shell:
//   • a page-level OBSERVATIONAL DISCLAIMER bar above every panel, and
//   • the AMBULATORY GLUCOSE PROFILE (AGP) — median + IQR + 5–95 bands
//     over the 24h time-of-day axis, computed CLIENT-SIDE from the
//     readings already loaded for the window (no new endpoint).
//
// Cycle 178 adds the PATTERNS & INSIGHTS layer (<PatternsPanel>): a 24h
// time-of-day distribution of alarm episodes (highs up / lows down,
// overnight emphasized), 2–4 deterministic DESCRIPTIVE insights, a facts
// strip (best/toughest day + in-target streak), and a CGM coverage read
// that flags reduced reliability — all client-side from the alarm rows +
// daily summaries already loaded (no LLM, no new endpoint).
//
// Cycle 179 adds the ALARM ACTIVITY panel (<AlarmActivityPanel>): a recent
// history of alarm episodes (severity, value, time, resolution) for the
// viewed member + window, read-only from the alerts already loaded.
// Channels are omitted — per-rail delivery rows are admin-gated only, so
// no member-facing endpoint exposes them (see the component header).
// The forward-projection cone and PDF reports remain deliberately later
// cycles.

import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";

import * as alertsApi from "../../api/alerts";
import * as analyticsApi from "../../api/analytics";
import * as glucoseApi from "../../api/glucose";
import * as treatmentsApi from "../../api/treatments";
import { AgpChart } from "../../components/glucose/AgpChart";
import { HistoryChart } from "../../components/glucose/HistoryChart";
import { TIRBar } from "../../components/glucose/TIRBar";
import { TrendArrow } from "../../components/glucose/TrendArrow";
import { DisclaimerBar } from "../../components/web/DisclaimerBar";
import { MemberAvatar } from "../../components/ui/MemberAvatar";
import { StatCard } from "../../components/ui/StatCard";
import { Tabs } from "../../components/ui/Tabs";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import type { GlucoseSocketState } from "../../hooks/useGlucoseSocket";
import { useSettings } from "../../hooks/useSettings";
import { useThresholds } from "../../hooks/useThresholds";
import { selectCurrentGlucoseFor } from "../../lib/currentGlucose";
import { followerStoppedLine, serviceLockedFor } from "../../lib/serviceLock";
import {
  displayValue,
  fmtAge,
  trendInfo,
  zoneColor,
  zoneLabel,
  zoneOf,
} from "../../lib/glucose";
import type {
  Alert,
  DailySummary,
  GlucoseSummary,
  Reading,
  Treatment,
} from "../../lib/types";
import { AlarmActivityPanel } from "./AlarmActivityPanel";
import { LiveFocusPanel } from "./LiveFocusPanel";
import { PatternsPanel } from "./PatternsPanel";
import styles from "./DashboardScreen.module.css";

type WindowId = "6h" | "12h" | "24h" | "7d" | "30d";
const WINDOWS: { id: WindowId; label: string; hours: number }[] = [
  { id: "6h", label: "6h", hours: 6 },
  { id: "12h", label: "12h", hours: 12 },
  { id: "24h", label: "24h", hours: 24 },
  { id: "7d", label: "7d", hours: 24 * 7 },
  { id: "30d", label: "30d", hours: 24 * 30 },
];

interface Props {
  variant: FrameVariant;
  socket: GlucoseSocketState;
  units: "mg/dL" | "mmol/L";
  now: number;
  memberName: string | null;
  memberAvatarId: string | null;
  activeMemberId: string | null;
}

export function DashboardScreen({
  variant,
  socket,
  units,
  now,
  memberName,
  memberAvatarId,
  activeMemberId,
}: Props) {
  const thresholds = useThresholds();
  const { settings } = useSettings();
  const h24 = settings?.display_24h ?? false;

  const [windowId, setWindowId] = useState<WindowId>("24h");
  const [readings, setReadings] = useState<Reading[]>([]);
  const [treatments, setTreatments] = useState<Treatment[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  // Cycle 178: alarm rows across ~2× the window (current + the equal-length
  // prior period) feed the Patterns trend insight; daily summaries feed the
  // facts strip. Both are read-side only, no new endpoint.
  const [alertsWide, setAlertsWide] = useState<Alert[]>([]);
  const [daily, setDaily] = useState<DailySummary[]>([]);
  const [summary, setSummary] = useState<GlucoseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [endMs, setEndMs] = useState(() => Date.now());

  const hours = WINDOWS.find((w) => w.id === windowId)?.hours ?? 24;
  const windowLabel = WINDOWS.find((w) => w.id === windowId)?.label ?? "window";
  // Daily summaries for the facts strip — the window's worth of days,
  // clamped to the endpoint's [1, 90] range.
  const dailyDays = Math.min(90, Math.max(1, Math.ceil(hours / 24)));

  // Refetch on window change OR active-member switch (a tile selection in
  // the status strip switches the server-side active member; the alerts /
  // analytics / readings then reflect the newly viewed member).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const end = Date.now();
    setEndMs(end);
    (async () => {
      const [g, t, a, s, aw, d] = await Promise.all([
        glucoseApi.history(hours, 30_000),
        treatmentsApi.list(hours, 1_000),
        alertsApi.list(hours, 1_000),
        analyticsApi.summary(hours),
        alertsApi.list(hours * 2, 2_000),
        analyticsApi.daily(dailyDays),
      ]);
      if (cancelled) return;
      if (g.ok) {
        setReadings(
          [...g.data.readings].sort(
            (x, y) =>
              new Date(x.timestamp).getTime() - new Date(y.timestamp).getTime(),
          ),
        );
      }
      if (t.ok) setTreatments(t.data.treatments);
      if (a.ok) setAlerts(a.data.alerts);
      if (aw.ok) setAlertsWide(aw.data.alerts);
      if (d.ok) setDaily(d.data.days);
      if (s.ok) setSummary(s.data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [hours, dailyDays, activeMemberId]);

  const startMs = endMs - hours * 60 * 60 * 1000;

  // Lows / highs EPISODE counts (one alert row = one episode, edge-
  // triggered) for the selected window, with the urgent sub-count.
  const counts = useMemo(() => {
    let lows = 0;
    let urgentLows = 0;
    let highs = 0;
    let urgentHighs = 0;
    for (const a of alerts) {
      if (a.level === "low") lows++;
      else if (a.level === "urgent_low") {
        lows++;
        urgentLows++;
      } else if (a.level === "high") highs++;
      else if (a.level === "urgent_high") {
        highs++;
        urgentHighs++;
      }
    }
    return { lows, urgentLows, highs, urgentHighs };
  }, [alerts]);

  // Live header readout for the viewed (active) member.
  // Cycle 433: the dashboard's own readout follows the same display rule.
  // ⚠ Cycle 435: through the ONE selector, which samples `Date.now()` for
  // itself rather than reading the ticking `now` above.
  const view = selectCurrentGlucoseFor(socket, activeMemberId);
  // Cycle 468: a followed member whose owner is locked out — the header
  // reads the stopped line, never an old number.
  const serviceLocked = serviceLockedFor(socket.serviceLockedByMember, activeMemberId);
  const current = serviceLocked ? null : view.displayValue;
  const hasReading = !serviceLocked && view.reading != null;
  const stale = current == null;
  const zone = current ? zoneOf(current.sgv, thresholds) : "unknown";
  const heroColor = stale ? "var(--ink-3)" : zoneColor(zone);
  const trend = current ? trendInfo(current.trend, current.trendRate) : null;
  const ageMin = view.ageMinutes;

  // Web-only landing — every other variant uses its own chrome/routes.
  if (variant !== "web") return <Navigate to="/now" replace />;

  return (
    <div className={styles.root}>
      {/* Page-level observational disclaimer — full-width, above all
          panels. Covers every figure on the dashboard. */}
      <DisclaimerBar />

      {/* Viewed-member header — updates when a status-strip tile is picked. */}
      <header className={styles.header}>
        <MemberAvatar avatarId={memberAvatarId} name={memberName ?? "Member"} size={52} />
        <div className={styles.headerText}>
          <div className={styles.headerName}>{memberName ?? "Member"}</div>
          <div className={styles.headerSub}>
            {hasReading ? (
              <>
                <span className={`${styles.headerSgv} tnum`} style={{ color: heroColor }}>
                  {current ? displayValue(current.sgv, units) : "—"}
                </span>
                {trend ? (
                  <TrendArrow glyph={trend.glyph} zone={zone} stale={false} size="20px" />
                ) : null}
                <span className={styles.headerMeta}>
                  {stale ? "stale" : zoneLabel(zone)}
                  {ageMin != null ? ` · ${fmtAge(ageMin)}` : ""}
                </span>
              </>
            ) : (
              <span className={styles.headerMeta} data-testid={serviceLocked ? "member-stopped-line" : undefined}>
                {serviceLocked ? followerStoppedLine(memberName ?? "this member") : "No recent reading"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Cycle 186: Live focus panel — the first content panel, leading the
          review stack. Carries the short 1h/2h/3h live chart + the relocated
          30-min projection cone (which collapsed to clipped pixels on the
          7d/30d review chart). Web-frame-only; additive above everything. */}
      <LiveFocusPanel
        readings={readings}
        treatments={treatments}
        alerts={alerts}
        thresholds={thresholds}
        units={units}
        h24={h24}
        nowMs={now}
        current={current}
      />

      {/* Long-range review trace — the existing interactive chart, reused
          as-is. Cycle 186 RELOCATED the forward-projection cone OFF this chart
          (now history-only on every window incl. 7d/30d) onto the Live panel
          above; the `projectForward` capability remains a valid default-off
          prop on <HistoryChart>, just no longer consumed here. */}
      <section className={styles.chartCard}>
        {/* The window selector lives with the chart it scopes (the review
            stack below), not up in the page header — keeps the control beside
            the graph it drives. */}
        <div className={styles.chartHead}>
          <span className={styles.cardLabel}>History</span>
          <div className={styles.windowTabs}>
            <Tabs<WindowId>
              tabs={WINDOWS.map((w) => ({ id: w.id, label: w.label }))}
              activeId={windowId}
              onChange={setWindowId}
            />
          </div>
        </div>
        {loading && readings.length === 0 ? (
          <div className={styles.chartLoading}>Loading…</div>
        ) : (
          <HistoryChart
            readings={readings}
            treatments={treatments}
            alerts={alerts}
            thresholds={thresholds}
            rangeStartMs={startMs}
            rangeEndMs={endMs}
            height={360}
            units={units}
            h24={h24}
            showTooltips
            // Cycle 186.3: every window starts at the FULL span (a slice
            // equal to fullSpan → byte-identical initial view) but is now
            // wheel/pinch zoomable on the dashboard review chart too — incl.
            // 7d/30d — matching the smooth zoom on the /history screen.
            panSliceMs={hours * 3_600_000}
            pinchZoom
            wheelZoom
            showDayMarkers={hours >= 24}
          />
        )}
      </section>

      {/* Ambulatory Glucose Profile — percentile bands over a 24h
          time-of-day axis, computed client-side from the window's
          readings. Shows its own "needs ~14 days" empty state. */}
      <section className={styles.agpCard}>
        <div className={styles.agpHead}>
          <span className={styles.cardLabel}>Glucose profile (AGP)</span>
          <span className={styles.agpHint}>
            every day in this window, folded onto one 24-hour day
          </span>
        </div>
        {loading && readings.length === 0 ? (
          <div className={styles.chartLoading}>Loading…</div>
        ) : (
          <AgpChart readings={readings} thresholds={thresholds} units={units} />
        )}
      </section>

      {/* Analytics summary — existing TIR + GMI/Avg/CV primitives. */}
      <section className={styles.statsGrid}>
        <div className={styles.tirCard}>
          <span className={styles.cardLabel}>Time in range</span>
          <TIRBar
            tir={
              summary?.tir ?? {
                urgent_low_pct: 0,
                low_pct: 0,
                in_range_pct: 0,
                high_pct: 0,
                urgent_high_pct: 0,
              }
            }
            height={20}
          />
        </div>
        <StatCard
          glass
          label="Avg"
          value={summary?.avg_sgv != null ? displayValue(summary.avg_sgv, units) : "—"}
          unit={units}
        />
        <StatCard
          glass
          label="GMI"
          value={summary?.gmi_pct != null ? summary.gmi_pct.toFixed(1) : "—"}
          unit="%"
        />
        <StatCard
          glass
          label="CV"
          value={summary?.cv_pct != null ? summary.cv_pct.toFixed(0) : "—"}
          unit="%"
        />
      </section>

      {/* Lows & highs episode counts (client-side from the alerts list). */}
      <section className={styles.countsRow}>
        <div className={styles.countCard}>
          <span className={styles.cardLabel}>Lows</span>
          <div className={styles.countValue} style={{ color: zoneColor("low") }}>
            {counts.lows}
          </div>
          <div className={styles.countSub}>
            {counts.urgentLows} urgent · episodes / {WINDOWS.find((w) => w.id === windowId)?.label}
          </div>
        </div>
        <div className={styles.countCard}>
          <span className={styles.cardLabel}>Highs</span>
          <div className={styles.countValue} style={{ color: zoneColor("high") }}>
            {counts.highs}
          </div>
          <div className={styles.countSub}>
            {counts.urgentHighs} urgent · episodes / {WINDOWS.find((w) => w.id === windowId)?.label}
          </div>
        </div>
      </section>

      {/* Alarm activity — a recent history of alarm episodes (severity,
          value, time, resolution) for the viewed member + window. Read-
          only from the alerts already loaded; channels omitted (no
          member-facing delivery endpoint exists — see the component). */}
      {loading && alerts.length === 0 ? null : (
        <AlarmActivityPanel
          alerts={alerts}
          units={units}
          h24={h24}
          now={now}
          windowLabel={windowLabel}
        />
      )}

      {/* Patterns & insights — 24h episode distribution, descriptive
          takeaways, facts strip, and CGM coverage. Client-side from the
          alarm rows + daily summaries already loaded. */}
      {loading && alertsWide.length === 0 && daily.length === 0 ? null : (
        <PatternsPanel
          alertsWide={alertsWide}
          readings={readings}
          daily={daily}
          hours={hours}
          endMs={endMs}
        />
      )}
    </div>
  );
}
